import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FolderOpen, Lightbulb, Loader2, Mail, StickyNote, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, downloadFile, errMsg } from "@/lib/api";
import { Markdown, PageHeader, fmtDate } from "@/components/Bits";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FolderBar, FolderPicker } from "@/components/PlaybookFolders";
import { EmailDialog, SharedLinks } from "@/components/PlaybookShare";
import { FOLDER_TIPS } from "@/lib/folderTips";

const NoteEditor = ({ item, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(item.note || "");
  const save = async () => {
    try {
      const { data } = await api.patch(`/playbook/${item.id}`, { note });
      onSaved(data); setOpen(false); toast.success("Note saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!open) {
    return item.note
      ? <blockquote onClick={() => setOpen(true)} className="mt-5 cursor-pointer rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm" data-testid={`playbook-note-${item.id}`}><span className="font-medium">My note:</span> {item.note}</blockquote>
      : <Button size="sm" variant="ghost" onClick={() => setOpen(true)} data-testid={`playbook-add-note-${item.id}`} className="rounded-full text-xs mt-4"><StickyNote className="h-4 w-4 mr-1.5" />Add a note</Button>;
  }
  return (
    <div className="mt-5 space-y-2">
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000} data-testid={`playbook-note-input-${item.id}`}
        placeholder="e.g. Try this in March, ask Sam to set up the email" className="bg-white text-sm" />
      <div className="flex gap-2">
        <Button size="sm" onClick={save} data-testid={`playbook-note-save-${item.id}`} className="rounded-full bg-navy hover:bg-navy/90 text-white">Save note</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
      </div>
    </div>
  );
};

const FolderTags = ({ item, folders, onChange }) => {
  const names = folders.filter((f) => (item.folder_ids || []).includes(f.id)).map((f) => f.name);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`playbook-folders-btn-${item.id}`} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-navy">
          <FolderOpen className="h-4 w-4" />{names.length ? names.join(", ") : "Add to folder"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2"><FolderPicker item={item} folders={folders} onChange={onChange} /></PopoverContent>
    </Popover>
  );
};

const Entry = ({ item, folders, onChange, onRemove }) => (
  <article className="rounded-2xl border border-border bg-card p-6 lg:p-8 fade-up" data-testid={`playbook-item-${item.id}`}>
    <div className="flex items-start justify-between gap-4 mb-4">
      <span className="text-xs font-mono text-muted-foreground">Saved {fmtDate(item.created_at)}{item.followup_id && " · follow-up"}</span>
      <button onClick={() => onRemove(item.id)} aria-label="Remove" data-testid={`playbook-remove-${item.id}`} className="text-muted-foreground hover:text-rose-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
    </div>
    <h3 className="text-lg font-semibold mb-1">{item.question}</h3>
    {item.parent_question && <p className="text-xs text-muted-foreground italic" data-testid={`playbook-parent-${item.id}`}>Follow-up to: {item.parent_question}</p>}
    <div className="mt-4"><Markdown>{item.answer}</Markdown></div>
    <NoteEditor item={item} onSaved={onChange} />
    <div className="mt-5 pt-4 border-t border-border"><FolderTags item={item} folders={folders} onChange={onChange} /></div>
  </article>
);

export default function Playbook() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [folders, setFolders] = useState([]);
  const [active, setActive] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [sentKey, setSentKey] = useState(0);
  useEffect(() => {
    api.get("/playbook").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/playbook/folders").then((r) => setFolders(r.data)).catch(() => {});
  }, []);
  const folder = folders.find((f) => f.id === active);
  const shown = (items || []).filter((i) => !active || (i.folder_ids || []).includes(active));

  const remove = async (id) => {
    try { await api.delete(`/playbook/${id}`); setItems((l) => l.filter((x) => x.id !== id)); toast.success("Removed"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const download = async () => {
    setBusy(true);
    try { await downloadFile(active ? `/playbook/pdf?folder_id=${active}` : "/playbook/pdf", `${folder ? folder.name : "My-Roots-Playbook"}.pdf`); }
    catch { toast.error("Couldn't create the PDF. Please try again."); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl">
      <PageHeader eyebrow="My playbook" title="Your saved answers." />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <p className="text-sm text-muted-foreground max-w-lg">Answers and follow-ups you've saved from Ask Lindsay, plus your filled-in templates. Sort them into folders, add your own notes, then download or email any folder as a branded PDF.</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setEmailOpen(true)} disabled={!shown.length} data-testid="playbook-email-btn" className="rounded-full">
            <Mail className="h-4 w-4 mr-2" />Email me a link
          </Button>
          <Button onClick={download} disabled={busy || !shown.length} data-testid="playbook-download-pdf" className="rounded-full bg-pinkb hover:bg-pinkb/90 text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}{folder ? "Download folder" : "Download PDF"}
          </Button>
        </div>
      </div>
      {(!!items?.length || !!folders.length) && <FolderBar folders={folders} setFolders={setFolders} items={items || []} active={active} setActive={setActive} onDeleted={() => setSentKey((k) => k + 1)} />}
      <EmailDialog open={emailOpen} onOpenChange={setEmailOpen} folder={folder} onSent={() => setSentKey((k) => k + 1)} />
      {items === null && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}
      {items?.length === 0 && !active && (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="playbook-empty">
          <p className="text-muted-foreground">Nothing saved yet. Tap "Save to playbook" under any answer.</p>
          <Link to="/app/ask" className="inline-block mt-4 text-pinkb hover:underline text-sm" data-testid="playbook-go-ask">Go to Ask Lindsay</Link>
        </div>
      )}
      <div className="space-y-6">
        {shown.map((i) => <Entry key={i.id} item={i} folders={folders} onRemove={remove} onChange={(d) => setItems((l) => l.map((x) => (x.id === d.id ? d : x)))} />)}
        {active && !shown.length && (
          <div className="rounded-2xl border border-dashed border-border p-6" data-testid="playbook-folder-empty">
            {FOLDER_TIPS[folder?.name] && <p className="flex gap-2 text-sm text-navy mb-2" data-testid="playbook-folder-tip"><Lightbulb className="h-4 w-4 shrink-0 mt-0.5 text-sun" />{FOLDER_TIPS[folder.name]}</p>}
            <p className="text-sm text-muted-foreground">{items?.length ? <>Nothing in this folder yet. Go to "All" and use "Add to folder" on any answer.</> : <>Nothing in this folder yet. Tap "Save to playbook" under any answer in <Link to="/app/ask" className="text-pinkb hover:underline" data-testid="playbook-folder-go-ask">Ask Lindsay</Link>.</>}</p>
          </div>
        )}
      </div>
      <SharedLinks reloadKey={sentKey} />
    </div>
  );
}
